import { ReactNode, useEffect } from "react";
import { Alert, ConfigProvider, Flex, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { Navigate } from "react-router-dom";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { translate } from "shared/languages";
import { AdminCapability } from "shared/utils/capabilities";

// Pembatasan Admin gates, one per treatment. The house rule they encode:
// in-context ACTIONS are plainly disabled with NO hover reason (an owner
// folds the restriction into `disabled` and skips its popover; Andrew dropped
// the "Dibatasi oleh Manager" tooltip on 2026-09-03), whole PAGES stay
// viewable but read-only (ReadOnlyWhenRestricted), and navigation-level
// entries are hidden by their owners with the hidden ROUTE guarded
// (RequireCapability). The server answers 403 behind every one of these, so
// a gate here is a courtesy, never the only line.

// One line and no description: the page is read-only, nothing more to say.
// `title` reuses the same lock line for a deactivated client's pages.
export const RestrictedBanner = ({ title }: { title?: string } = {}) => (
  <Alert
    type="info"
    showIcon
    icon={<LockOutlined />}
    message={title ?? translate("capability.banner_title")}
  />
);

/**
 * Renders `children` read-only when the capability is restricted: a lock
 * banner, then AntD's DisabledContext over everything beneath (Button, Switch,
 * Input, Select, InputNumber, DatePicker, Upload, table row buttons and modal
 * footers alike, since context crosses portals). Wrap cards and forms only,
 * never a page heading, or its back arrow dies with the rest. Two things the
 * context cannot reach and the owner must handle itself: an explicit
 * `disabled={false}` prop (it beats the context) and a Table `onRow` click.
 */
export const ReadOnlyWhenRestricted = ({
  capability,
  children,
}: {
  capability: AdminCapability;
  children: ReactNode;
}) => {
  const { isRestricted } = useAdminCapabilities();
  if (!isRestricted(capability)) return <>{children}</>;
  return (
    <Flex vertical gap={16}>
      <RestrictedBanner />
      <ConfigProvider componentDisabled>{children}</ConfigProvider>
    </Flex>
  );
};

/**
 * Route gate for entries the owner hides (the money reports, which the
 * Laporan index leaves out of its row list, and the create-product form). Same shape as RequireRole, plus a
 * toast so a deep link explains itself. Renders nothing while a plain Admin's
 * flags are still loading, so a legitimate visit never flashes a redirect.
 */
export const RequireCapability = ({
  capability,
  fallback,
  children,
}: {
  capability: AdminCapability;
  fallback: string;
  children: ReactNode;
}) => {
  const { isRestricted, isLoading } = useAdminCapabilities();
  const restricted = !isLoading && isRestricted(capability);
  useEffect(() => {
    if (restricted) message.warning(translate("capability.restricted_page"));
  }, [restricted]);
  if (isLoading) return null;
  return restricted ? <Navigate to={fallback} replace /> : <>{children}</>;
};
